// One place that maps app concepts to icons (lucide, drawn with react-native-svg on every
// platform). Screens use <Icon name="timer" /> so swapping the icon set later is one file.
import React from 'react';
// Per-icon imports: Metro has no tree-shaking, so importing from the package root would
// bundle all ~1,900 icons into the native app.
import ArrowDown from 'lucide-react-native/icons/arrow-down';
import ArrowUp from 'lucide-react-native/icons/arrow-up';
import Banknote from 'lucide-react-native/icons/banknote';
import BookOpen from 'lucide-react-native/icons/book-open';
import CalendarDays from 'lucide-react-native/icons/calendar-days';
import CalendarPlus from 'lucide-react-native/icons/calendar-plus';
import Camera from 'lucide-react-native/icons/camera';
import Gift from 'lucide-react-native/icons/gift';
import Link from 'lucide-react-native/icons/link';
import Check from 'lucide-react-native/icons/check';
import Pin from 'lucide-react-native/icons/pin';
import CheckCircle2 from 'lucide-react-native/icons/circle-check';
import ChefHat from 'lucide-react-native/icons/chef-hat';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import Circle from 'lucide-react-native/icons/circle';
import Clipboard from 'lucide-react-native/icons/clipboard';
import Clock from 'lucide-react-native/icons/clock';
import House from 'lucide-react-native/icons/house';
import Compass from 'lucide-react-native/icons/compass';
import Copy from 'lucide-react-native/icons/copy';
import Crown from 'lucide-react-native/icons/crown';
import Download from 'lucide-react-native/icons/download';
import Droplet from 'lucide-react-native/icons/droplet';
import Flame from 'lucide-react-native/icons/flame';
import HelpCircle from 'lucide-react-native/icons/circle-question-mark';
import ImageIcon from 'lucide-react-native/icons/image';
import Info from 'lucide-react-native/icons/info';
import Leaf from 'lucide-react-native/icons/leaf';
import ListChecks from 'lucide-react-native/icons/list-checks';
import Lock from 'lucide-react-native/icons/lock';
import Minus from 'lucide-react-native/icons/minus';
import Moon from 'lucide-react-native/icons/moon';
import MoreHorizontal from 'lucide-react-native/icons/ellipsis';
import Package from 'lucide-react-native/icons/package';
import Pause from 'lucide-react-native/icons/pause';
import Pencil from 'lucide-react-native/icons/pencil';
import PieChart from 'lucide-react-native/icons/chart-pie';
import Play from 'lucide-react-native/icons/play';
import Plus from 'lucide-react-native/icons/plus';
import RotateCcw from 'lucide-react-native/icons/rotate-ccw';
import Scale from 'lucide-react-native/icons/scale';
import ScrollText from 'lucide-react-native/icons/scroll-text';
import Search from 'lucide-react-native/icons/search';
import Settings from 'lucide-react-native/icons/settings';
import Share2 from 'lucide-react-native/icons/share-2';
import ShoppingCart from 'lucide-react-native/icons/shopping-cart';
import Snowflake from 'lucide-react-native/icons/snowflake';
import Sparkles from 'lucide-react-native/icons/sparkles';
import Sun from 'lucide-react-native/icons/sun';
import Sunrise from 'lucide-react-native/icons/sunrise';
import Timer from 'lucide-react-native/icons/timer';
import Trash2 from 'lucide-react-native/icons/trash';
import TriangleAlert from 'lucide-react-native/icons/triangle-alert';
import Upload from 'lucide-react-native/icons/upload';
import User from 'lucide-react-native/icons/user';
import Users from 'lucide-react-native/icons/users';
import Utensils from 'lucide-react-native/icons/utensils';
import X from 'lucide-react-native/icons/x';
import Carrot from 'lucide-react-native/icons/carrot';
import CookingPot from 'lucide-react-native/icons/cooking-pot';
import Library from 'lucide-react-native/icons/library';
import Globe from 'lucide-react-native/icons/globe';
import Type from 'lucide-react-native/icons/type';
import Vegan from 'lucide-react-native/icons/vegan';
import Sprout from 'lucide-react-native/icons/sprout';
import WheatOff from 'lucide-react-native/icons/wheat-off';
import MilkOff from 'lucide-react-native/icons/milk-off';
import Bone from 'lucide-react-native/icons/bone';
import CandyOff from 'lucide-react-native/icons/candy-off';
import Fish from 'lucide-react-native/icons/fish';
import Egg from 'lucide-react-native/icons/egg';
import Flower2 from 'lucide-react-native/icons/flower-2';
import Church from 'lucide-react-native/icons/church';
import DropletOff from 'lucide-react-native/icons/droplet-off';
import PartyPopper from 'lucide-react-native/icons/party-popper';
import MessageCircle from 'lucide-react-native/icons/message-circle';
import Flag from 'lucide-react-native/icons/flag';
import Ban from 'lucide-react-native/icons/ban';
import Bot from 'lucide-react-native/icons/bot';
import Inbox from 'lucide-react-native/icons/inbox';
import Mic from 'lucide-react-native/icons/mic';
import MicOff from 'lucide-react-native/icons/mic-off';
import ScanText from 'lucide-react-native/icons/scan-text';
import Volume2 from 'lucide-react-native/icons/volume-2';
import WandSparkles from 'lucide-react-native/icons/wand-sparkles';
import Send from 'lucide-react-native/icons/send';
import { usePalette } from '../theme';

const ICONS = {
  path: Compass,
  home: House,
  planner: CalendarDays,
  library: Library,
  search: Search,
  recipe: ChefHat,
  cookbook: BookOpen,
  mealplan: CalendarDays,
  ingredient: Carrot,
  utensil: CookingPot,
  unit: Scale,
  country: Globe,
  clock: Clock,
  servings: Users,
  price: Banknote,
  diet: Leaf,
  pantry: Package,
  grocery: ShoppingCart,
  cartAdd: ShoppingCart,
  check: Check,
  pin: Pin,
  checkCircle: CheckCircle2,
  circle: Circle,
  close: X,
  plus: Plus,
  minus: Minus,
  edit: Pencil,
  trash: Trash2,
  share: Share2,
  gift: Gift,
  link: Link,
  copy: Copy,
  clipboard: Clipboard,
  camera: Camera,
  image: ImageIcon,
  play: Play,
  pause: Pause,
  timer: Timer,
  reset: RotateCcw,
  streak: Flame,
  freeze: Snowflake,
  super: Crown,
  sparkles: Sparkles,
  settings: Settings,
  info: Info,
  help: HelpCircle,
  legal: ScrollText,
  user: User,
  more: MoreHorizontal,
  back: ChevronLeft,
  forward: ChevronRight,
  up: ArrowUp,
  down: ArrowDown,
  breakfast: Sunrise,
  lunch: Sun,
  dinner: Moon,
  nutrition: PieChart,
  calendarAdd: CalendarPlus,
  warning: TriangleAlert,
  lock: Lock,
  export: Upload,
  import: Download,
  checklist: ListChecks,
  dirty: Droplet,
  meal: Utensils,
  textSize: Type,
  holiday: PartyPopper,
  // Diet symbols (see DIET_SYMBOLS in logic/diets.ts)
  vegan: Vegan,
  sprout: Sprout,
  wheatOff: WheatOff,
  milkOff: MilkOff,
  bone: Bone,
  candyOff: CandyOff,
  sun: Sun,
  fish: Fish,
  egg: Egg,
  flower: Flower2,
  flame: Flame,
  church: Church,
  buddies: MessageCircle,
  report: Flag,
  block: Ban,
  send: Send,
  dropletOff: DropletOff,
  assistant: Bot,
  inbox: Inbox,
  mic: Mic,
  micOff: MicOff,
  scan: ScanText,
  speak: Volume2,
  magic: WandSparkles,
} as const;

export type IconName = keyof typeof ICONS;

export default function Icon({ name, size = 22, color, strokeWidth = 2.5 }: { name: IconName; size?: number; color?: string; strokeWidth?: number }) {
  const palette = usePalette();
  const Glyph = ICONS[name];
  return <Glyph size={size} color={color ?? palette.text} strokeWidth={strokeWidth} />;
}
